import { useEcho } from "@laravel/echo-react";
import { useQueryClient } from "@tanstack/react-query";
import { notify } from "@/lib/notify";
import { router } from "@/router";
import { Bell, MessageSquare, BookOpen, Video, UserCheck, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { sound } from "@/lib/sound";
import { useAuthStore } from "@/store/auth.store";
import { notificationsKeys } from "@/hooks/useNotifications";
import { messagingKeys } from "@/hooks/useMessaging";
import { CHANNELS, EVENTS } from "@/config/echo-channels";
import { claimNotificationToast } from "@/lib/notification-dedupe";
import type { ApiUserNotification } from "@/services/notification.service";
import type { ApiConversation } from "@/types";

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; bg: string; accent: string }> = {
  message_received: { icon: <MessageSquare size={20} />, bg: "#1e3a5f", accent: "#60a5fa" },
  teacher_message: { icon: <MessageSquare size={20} />, bg: "#1e3a5f", accent: "#60a5fa" },
  student_message: { icon: <MessageSquare size={20} />, bg: "#1e3a5f", accent: "#60a5fa" },
  classroom_chat_message: { icon: <MessageSquare size={20} />, bg: "#1e3a5f", accent: "#60a5fa" },
  homework_submitted: { icon: <BookOpen size={20} />, bg: "#78350f", accent: "#fbbf24" },
  homework_assigned: { icon: <BookOpen size={20} />, bg: "#78350f", accent: "#fbbf24" },
  homework_graded: { icon: <BookOpen size={20} />, bg: "#78350f", accent: "#fbbf24" },
  live_session_started: { icon: <Video size={20} />, bg: "#064e3b", accent: "#34d399" },
  live_session_ended: { icon: <Video size={20} />, bg: "#064e3b", accent: "#34d399" },
  enrollment_approved: { icon: <UserCheck size={20} />, bg: "#3b0764", accent: "#a78bfa" },
  student_enrolled: { icon: <UserCheck size={20} />, bg: "#3b0764", accent: "#a78bfa" },
};

const DEFAULT_CONFIG = { icon: <Bell size={20} />, bg: "#1e293b", accent: "#818cf8" };

function NotificationToast({
  title,
  body,
  type,
  toastId,
  onNavigate,
}: {
  title: string;
  body: string;
  type: string;
  toastId: string | number;
  onNavigate?: () => void;
}) {
  const cfg = TYPE_CONFIG[type] ?? DEFAULT_CONFIG;

  return (
    <div
      style={{ background: cfg.bg }}
      onClick={
        onNavigate
          ? () => {
              onNavigate();
              notify.dismiss(toastId);
            }
          : undefined
      }
      className={cn(
        "flex items-start gap-3 px-4 py-3.5 rounded-2xl shadow-2xl w-[340px] border border-white/10",
        onNavigate && "cursor-pointer hover:brightness-110 transition-all",
      )}
    >
      <div
        style={{ color: cfg.accent, background: `${cfg.accent}20` }}
        className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
      >
        {cfg.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-black text-sm leading-tight">{title}</p>
        <p className="text-white/60 text-xs font-medium mt-0.5 leading-snug">{body}</p>
        {onNavigate && (
          <p className="text-white/40 text-[10px] font-black uppercase tracking-widest mt-1.5">
            Cliquer pour voir →
          </p>
        )}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          notify.dismiss(toastId);
        }}
        className="shrink-0 mt-0.5 p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Fermer"
      >
        <X size={14} />
      </button>
    </div>
  );
}

function getClassUrl(type: string, meta: Record<string, unknown>, role: string): string | null {
  if (type !== "classroom_chat_message") return null;

  const base =
    role === "admin"
      ? "/admin"
      : role === "teacher"
        ? "/teacher"
        : role === "family"
          ? "/parent"
          : "/student";

  if (role === "admin") {
    const id = meta.class_id;
    if (id) return `${base}/classes/${id}`;
  } else {
    const uuid = meta.class_uuid ?? meta.class_id;
    if (uuid) return `${base}/classes/${uuid}`;
  }
  return null;
}

interface UnreadCountUpdatedEvent {
  conversation_id?: number;
  unread_count: number | null;
  unread_delta: number;
}

interface AnnouncementPublishedEvent {
  id: number;
  title: string;
  content: string;
  message_type: string;
  target_audience: string;
}

export function useNotificationChannel() {
  const userId = useAuthStore((s) => s.user?.id);
  const role = useAuthStore((s) => s.role);
  const qc = useQueryClient();
  const channel = userId ? CHANNELS.userNotifications(userId) : "";
  const announcementsChannel = userId ? CHANNELS.announcements(userId) : "";

  useEcho<ApiUserNotification>(
    channel,
    EVENTS.notifications.created,
    (event) => {
      if (event.notification?.channel !== "in_app") {
        return;
      }

      // Le même envoi backend arrive aussi en push FCM (`PushSubscriber`, cf.
      // `notification-dedupe.ts`) — sur un onglet au premier plan, les deux canaux se déclenchent
      // à quelques ms d'écart pour la même notification. `notification_id` est l'identifiant
      // partagé par les deux payloads (l'id de la ligne `notifications`, pas l'id du pivot
      // `ApiUserNotification`). L'invalidation de cache ci-dessous reste inconditionnelle : seul
      // l'affichage du toast est déduppliqué.
      const showToast = !claimNotificationToast(event.notification_id);
      if (showToast) sound.notification();
      const meta = (event.notification.metadata ?? {}) as Record<string, unknown>;
      const classUrl = getClassUrl(event.notification.type, meta, role);

      if (showToast) {
        notify.custom(
          (t) => (
            <NotificationToast
              title={event.notification.title}
              body={event.notification.body}
              type={event.notification.type}
              toastId={t}
              onNavigate={classUrl ? () => router.navigate(classUrl) : undefined}
            />
          ),
          { duration: 5000 },
        );
      }
      qc.invalidateQueries({ queryKey: notificationsKeys.all });

      // Pas d'invalidation de `conversations`/`unreadCount` ici pour les messages : le même
      // envoi déclenche aussi `unreadCountUpdated` ci-dessous, qui met à jour le cache de façon
      // incrémentale (ou invalide si besoin) sans provoquer un second refetch concurrent. Les
      // deux événements arrivant à quelques ms d'écart, invalider ici en plus annulait le refetch
      // de l'un au profit de l'autre en boucle — visible côté réseau comme des requêtes
      // `/v1/conversations` répétées et marquées `(canceled)`.

      if (
        event.notification.type === "live_session_started" &&
        (role === "student" || role === "family")
      ) {
        qc.invalidateQueries({ queryKey: ["student-class-detail"] });
      }
    },
    [userId],
    "private",
  );

  useEcho<UnreadCountUpdatedEvent>(
    channel,
    EVENTS.notifications.unreadCountUpdated,
    (event) => {
      qc.invalidateQueries({ queryKey: messagingKeys.unreadCount });

      if (event.conversation_id != null) {
        // Même clé que `useConversations` (suffixée par l'id utilisateur) : avec la clé nue, la
        // lecture renvoyait toujours `undefined`, la conversation passait pour inconnue et chaque
        // événement relançait un refetch complet de `/v1/conversations` (429 en rafale sur le
        // dashboard enseignant, cf. SUCCESCHOOL-FRONTEND-V2 « Too Many Attempts »).
        const conversationsKey = [...messagingKeys.conversations, userId];
        const cached = qc.getQueryData<ApiConversation[]>(conversationsKey);
        const known = cached?.some((c) => c.id === event.conversation_id) ?? false;
        if (known) {
          qc.setQueryData<ApiConversation[]>(conversationsKey, (prev) => {
            if (!prev) return prev;
            return prev.map((c) =>
              c.id === event.conversation_id
                ? { ...c, unread_count: (c.unread_count ?? 0) + event.unread_delta }
                : c,
            );
          });
        } else {
          // New conversation not yet in the cached list — refetch so it appears.
          qc.invalidateQueries({ queryKey: messagingKeys.conversations });
        }
      } else {
        qc.invalidateQueries({ queryKey: messagingKeys.conversations });
      }
    },
    [userId],
    "private",
  );

  useEcho<AnnouncementPublishedEvent>(
    announcementsChannel,
    EVENTS.announcements.published,
    (event) => {
      notify.show(`📢 ${event.title}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ["announcements"] });
    },
    [userId],
    "private",
  );
}
